import React, { useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding as faBuildingRegular, faShield as faShieldRegular } from '@fortawesome/pro-regular-svg-icons'
import { faArrowRightArrowLeft, faShield } from '@fortawesome/pro-solid-svg-icons'
import { HeaderLogo } from './header-logo'
import { lmsAdminVisible } from '../../view/lms-admin/lms-admin-gate'
import { useLmsAccess } from '../../view/lms-admin/lms-access'
import { useLmsEnabled } from '../../view/lms-admin/use-lms-enabled'
import { OffcanvasClose } from '../../shared/component/offcanvas'
import { Tooltip } from '../../shared/component/floating'
import { IconAnalytic, IconCaretDown, IconCaretUp, IconDocs, IconWebViewer } from '../../shared/icon/icon'
import { SECTION, selectedEntry, sidebarEntry } from '../../shared/navigation/sections'
import { useAuthContext } from '../../context/auth.context'
import { ROLE_ORG, ROLE_SYS } from '../../role/role.context'
import { useRoleOrg, useRoleOrgOrganization, useRoleOrgSSO } from '../../role/hook/role-org'
import { useRoleSys } from '../../role/hook/role-sys'
import { useDocAuthRedirect } from '../../layout/unauthorized/auth/hooks/doc'
import { useStaffTools } from './staff-tools'
import { useViewerLaunch } from '../../shared/hooks/use-viewer-launch'
import { useViewerAvailable } from '../../shared/hooks/use-viewer-available'
import { ViewerLaunchFailure } from '../../shared/viewer/viewer-launch-failure'
import { billingAvailable } from '../../layout/authorized/admin/billing/billing-api'
import { packagesAvailable } from '../../layout/authorized/admin/packages/packages-api'
import { canSwitchOrganization } from '../../shared/organization-switch'
import { useApiAccess } from '../../layout/authorized/org/api-keys/use-api-access'
import './sidebar-left.scss'

// The admin's left sidebar, "option D" (owner, 2026-10-07): the current
// organization's card (a switch when the person can switch) beside the
// Staff tools shield; the organization's menu in groups; and, when Staff
// tools is on, the EducationXR staff section on a darker band.
//
// Every entry keeps exactly the gate it had before the restructure: the
// gate components it used to sit in (DisplayAuth, RoleOrg*, RoleSys*,
// LoginSysAdmin, LoginSysRoot, DisplayOrgCompany, FeatureTradeShow) are
// replaced by the same tests on the same state, one `show` per entry, so a
// group can tell whether it has anything to show. sidebar-left-gates.test.js
// holds the table, recorded from the old sidebar.

// bg-body: white in light mode, the site ground in dark mode.
const Brand = () => {
  return (
    <div className="navbar bg-body">
      <div className="container-fluid">
        <div className="navbar-brand w-100">
          <div className="d-flex justify-content-around">
            <HeaderLogo />
          </div>
        </div>
      </div>
    </div>
  )
}

// The current organization. When the session can switch to another (the
// one decision the account menu's Change Organization shares,
// shared/organization-switch) it is the way to switch: a link to the picker
// page (/auth/account), not a dropdown, so it carries a switch icon rather
// than a caret. A long name wraps to two lines, then truncates; the title
// has it whole.
const OrganizationCard = ({ name, canSwitch, target }) => {
  const body = (
    <React.Fragment>
      <FontAwesomeIcon icon={faBuildingRegular} className="edxr-sb-org__icon" />
      <span className="edxr-sb-org__text">
        <span className="edxr-sb-org__name">{name}</span>
        <span className="edxr-sb-org__sub">Organization</span>
      </span>
      {canSwitch && <FontAwesomeIcon icon={faArrowRightArrowLeft} className="edxr-sb-org__switch" aria-hidden="true" />}
    </React.Fragment>
  )

  if (!canSwitch) {
    return <div className="edxr-sb-org" title={name} data-testid="sidebar-org-card">{body}</div>
  }

  return (
    <div className="edxr-sb-org-slot">
      <OffcanvasClose target={target}>
        <Link
          className="edxr-sb-org edxr-sb-org--switch"
          to="/auth/account"
          title={`${name || 'Organization'}: switch organization`}
          aria-label={`${name || 'Organization'}, Organization. Switch organization`}
          data-testid="sidebar-org-card">
          {body}
        </Link>
      </OffcanvasClose>
    </div>
  )
}

// Shows and hides the staff section. Staff only, never while impersonating.
const StaffToolsButton = ({ on, onToggle }) => {
  const label = on ? 'Hide internal tools' : 'Show internal tools'

  return (
    <Tooltip label={<span className="edxr-sb-tip">{label}</span>} placement="bottom">
      <button type="button" className={`edxr-sb-shield${on ? ' is-on' : ''}`} aria-label="Internal tools" aria-pressed={on} onClick={onToggle}>
        <FontAwesomeIcon icon={on ? faShield : faShieldRegular} />
      </button>
    </Tooltip>
  )
}

// The entries look as they did before the rebuild (owner feedback
// 2026-10-07), with the new sentence-case labels: Bootstrap's nav-pills
// nav-link inside a padded nav-item -- 1rem light text, the large icons in a
// 40 px column, and the grey rounded pill inset from both edges on hover
// (the theme's .edxr-sidebar-left-item) and on the current page. Never
// underlined: the admin's global rule (src/scss/links.scss) covers every
// link and button, so the sidebar carries no opt-out of its own.
const ITEM_LI = 'nav-item p-1 fs-6'
const ITEM = 'edxr-sb-item nav-link d-flex align-items-center w-100 text-start fw-light edxr-sidebar-left-item edxr-pointer'
const LIST = 'nav nav-pills flex-column'

// The current page's entry is one entry, `current` (its `to`): the one whose
// section the address is in (shared/navigation/sections.js selectedEntry),
// so a page beside a section's list -- the SSO pages, a sign-in method, a
// build log -- lights the entry its trail starts at (audit F5).

const ItemBody = ({ Icon, title, children }) =>
  <React.Fragment>
    <span className="edxr-sb-item__icon text-center" style={{ width: 40 }} aria-hidden="true">{Icon && <Icon size="lg" />}</span>
    <span className="edxr-sb-item__label mx-2 flex-fill">{title}</span>
    {children}
  </React.Fragment>

const ItemLink = ({ Icon, title, to, target, current }) =>
  <li className={ITEM_LI}>
    <OffcanvasClose target={target}>
      <Link className={ITEM} to={to} aria-current={current === to ? 'page' : undefined}>
        <ItemBody Icon={Icon} title={title} />
      </Link>
    </OffcanvasClose>
  </li>

const ItemButton = ({ Icon, title, onClick, disabled, target }) =>
  <li className={ITEM_LI}>
    <OffcanvasClose target={target}>
      <button type="button" className={ITEM} onClick={onClick} disabled={disabled}>
        <ItemBody Icon={Icon} title={title} />
      </button>
    </OffcanvasClose>
  </li>

// An entry with pages under it (Analytics: General, Custom). The pages stay
// in the document, collapsed, indented under it as before.
const ItemCollapse = ({ Icon, title, items, target, current, startOpen }) => {
  const id = useId()
  const [open, updateOpen] = useState(!!startOpen)

  return (
    <li className={ITEM_LI}>
      <button type="button" className={ITEM} aria-expanded={open} aria-controls={id} onClick={() => updateOpen(!open)}>
        <ItemBody Icon={Icon} title={title}>
          <span className="edxr-sb-item__state" aria-hidden="true">{open ? <IconCaretUp size="lg" /> : <IconCaretDown size="lg" />}</span>
        </ItemBody>
      </button>
      <ul id={id} className={`edxr-sb-sub ${LIST} ms-3 collapse${open ? ' show' : ''}`}>
        {items.map(item => <ItemLink key={item.to} {...item} target={target} current={current} />)}
      </ul>
    </li>
  )
}

const Item = ({ item, target, current }) => {
  if (item.items) return <ItemCollapse {...item} target={target} current={current} startOpen={item.items.some(sub => sub.to === current)} />
  if (item.onClick) return <ItemButton {...item} target={target} />
  return <ItemLink {...item} target={target} current={current} />
}

// A group: its heading only above at least one entry it shows.
const Group = ({ title, tag, items, testId, target, current }) => {
  const id = useId()
  const shown = items.filter(item => item.show)

  if (!shown.length) return null

  return (
    <div className="edxr-sb-group" role="group" aria-labelledby={id}>
      <div className="edxr-sb-heading" id={id}>
        <span data-testid={testId}>{title}</span>
        {tag && <span className="edxr-sb-tag">{tag}</span>}
      </div>
      <ul className={`edxr-sb-list ${LIST}`}>
        {shown.map(item => <Item key={item.title} item={item} target={target} current={current} />)}
      </ul>
    </div>
  )
}

/// /////////////////////////////////////////////////

export const SidebarLeft = ({ target }) => {
  const authContext = useAuthContext()
  const location = useLocation()
  const { pathname } = location
  const docAuthRedirect = useDocAuthRedirect()
  const titleId = useId()

  const handleDocs = async () => docAuthRedirect()

  const state = authContext.state
  const claims = state.jwtClaims || {}

  // The gates, as the components they replace computed them.
  const isAuth = !!state.accessToken // DisplayAuth
  const staff = !!(state.isSysRoot || state.isSysAdmin) // LoginSysAdmin
  const root = !!state.isSysRoot // LoginSysRoot
  const company = claims.organizationType === 'company' // DisplayOrgCompany
  const tradeShow = !!claims.featureTradeShow // FeatureTradeShow
  const impersonating = !!claims.impersonationEndsAt

  // RoleOrg* (a super admin or root reads every organization scope)
  const dashboard = useRoleOrg(ROLE_ORG.DASHBOARD)
  const analytic = useRoleOrg(ROLE_ORG.ANALYTIC)
  const node = useRoleOrg(ROLE_ORG.NODE)
  const user = useRoleOrg(ROLE_ORG.USER)
  const group = useRoleOrg(ROLE_ORG.GROUP)
  const tradeshow = useRoleOrg(ROLE_ORG.TRADESHOW)
  const doc = useRoleOrg(ROLE_ORG.DOC)
  const auditLog = useRoleOrg(ROLE_ORG.AUDIT_LOG)
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const apiKey = useRoleOrg(ROLE_ORG.API_KEY)
  // the Settings rules, shared with the account menu
  const organization = useRoleOrgOrganization()
  const sso = useRoleOrgSSO()

  // RoleSys* (root reads every system scope)
  const sysApp = useRoleSys(ROLE_SYS.SYS_APP)
  const sysOrganization = useRoleSys(ROLE_SYS.SYS_ORGANIZATION)
  const sysLogin = useRoleSys(ROLE_SYS.SYS_LOGIN)
  const sysAuthStatus = useRoleSys(ROLE_SYS.SYS_AUTH_STATUS)
  const sysProvider = useRoleSys(ROLE_SYS.SYS_PROVIDER)
  const sysErrors = useRoleSys(ROLE_SYS.SYS_ERRORS)
  const sysRequestLog = useRoleSys(ROLE_SYS.SYS_REQUEST_LOG)
  const sysAuditLog = useRoleSys(ROLE_SYS.SYS_AUDIT_LOG)
  const sysAuditRetention = useRoleSys(ROLE_SYS.SYS_AUDIT_RETENTION)
  const sysAlertRouting = useRoleSys(ROLE_SYS.SYS_ALERT_ROUTING)
  const sysSendgridEvent = useRoleSys(ROLE_SYS.SYS_SENDGRID_EVENT)
  const sysLmsPreset = useRoleSys(ROLE_SYS.SYS_LMS_PRESET)
  const sysApiKey = useRoleSys(ROLE_SYS.SYS_API_KEY)
  const sysNotify = useRoleSys(ROLE_SYS.SYS_NOTIFY)
  const sysAiToolsTelemetry = useRoleSys(ROLE_SYS.SYS_AI_TOOLS_TELEMETRY)
  const sysPackages = useRoleSys(ROLE_SYS.SYS_PACKAGES)
  const sysBilling = useRoleSys(ROLE_SYS.SYS_BILLING)
  const sysConsentLog = useRoleSys(ROLE_SYS.SYS_CONSENT_LOG)
  const notify = Boolean(process.env.REACT_APP_API_NOTIFY) // the build knows the notify service (api-new/state/notify notifyAvailable)

  // Staff tools: the shield is for super admins and roots, never in an
  // impersonated session. A root impersonating a super admin holds a super
  // admin's token, whose staff entries the sidebar has always listed; with
  // no shield to open them, they are simply shown.
  const staffTools = useStaffTools({ allowed: staff && !impersonating, pathname })

  // Internal tools open at the top of the menu (owner 2026-10-07): turning
  // them on also scrolls the menu to the top, so what was just switched on
  // is on screen.
  const navRef = useRef(null)
  const toggleStaffTools = () => {
    const turningOn = !staffTools.on
    staffTools.toggle()
    if (turningOn && navRef.current) navRef.current.scrollTop = 0
  }
  const showStaff = staff && (impersonating || staffTools.on)

  // API keys (Phase D, Task 15): API_KEY read, and the organization's
  // partner API access on. Like the LMS, an organization without the feature
  // never sees the entry, staff included (owner 2026-11-02, replacing the
  // 2026-10-23 rule that showed it whatever the access): staff switch the
  // access on from the organization's page (Internal tools > Organizations),
  // and an organization's keys stay in Internal tools > API keys. A pending
  // or unreadable answer hides it, and only a session with API_KEY read asks
  // (use-api-access.js: once per session and organization).
  const apiKeysCouldShow = isAuth && !!apiKey.accessRead
  const apiAccess = useApiAccess(apiKeysCouldShow)
  const apiKeysAllowed = apiKeysCouldShow && !!apiAccess && apiAccess.enabled === true

  // The LMS entries (Analytics > LMS, Integrations > LMS) also need the
  // current organization to have the LMS feature. Staff hold every
  // organization permission, so the roles never say no in an organization
  // without it; the sign-in token has no such claim, so the LMS service is
  // asked (use-lms-enabled.js: once per organization; FEATURE_DISABLED hides,
  // a pending answer hides, any other failure shows). Only a session that
  // could see an LMS entry at all asks. The staff-only answer (lms-access.js)
  // is asked separately, once per page, and never waited for: until it
  // arrives the menu is the closed one.
  useLmsAccess() // whether the server has opened the LMS admin beyond staff (lms-access.js)
  const lmsCouldShow = isAuth && lmsAdminVisible(state) && (!!lms.accessRead || !!analytic.accessRead)
  const lmsEnabled = useLmsEnabled(lmsCouldShow)

  // Web viewer: the web viewer's home, in a new tab, for this session (a
  // launch code with no target, shared/hooks/use-viewer-launch.js, the code
  // path of a course's "Launch"). Open to every signed-in person since the
  // viewer opened to everyone (owner 2026-10-08; it was a super-admin staff
  // tool), an impersonated session included (it stays the impersonation).
  // Called straight from the click: the tab opens before any await. Shown
  // only where this environment has a viewer (auth's GET /launch/available,
  // read once per session): a launch there can only fail.
  const webViewerAllowed = isAuth
  const viewerAvailable = useViewerAvailable(webViewerAllowed)
  const viewerLaunch = useViewerLaunch()
  const handleWebViewer = () => {
    viewerLaunch.launch()
  }

  const orgGroups = [
    {
      title: 'Overview',
      items: [
        { ...sidebarEntry(SECTION.dashboard), show: isAuth && !!dashboard.accessRead },
        { title: 'Web viewer', Icon: IconWebViewer, onClick: handleWebViewer, disabled: viewerLaunch.launching, show: webViewerAllowed && viewerAvailable },
        {
          title: 'Analytics',
          Icon: IconAnalytic,
          show: isAuth && !!analytic.accessRead,
          items: [
            sidebarEntry(SECTION.analyticsGeneral),
            sidebarEntry(SECTION.analyticsCustom),
            // The LMS insights pages: whoever reads Analytics and sees the LMS
            // admin (system administrators and root while the staff-only
            // switch is closed, lms-access.js).
            ...(lmsAdminVisible(state) && lmsEnabled ? [sidebarEntry(SECTION.analyticsLms)] : [])
          ]
        }
      ]
    },
    {
      title: 'Library',
      items: [
        { ...sidebarEntry(SECTION.content), show: isAuth && !!node.accessRead },
        { ...sidebarEntry(SECTION.templates), show: isAuth && !!node.accessRead },
        { ...sidebarEntry(SECTION.assetLibrary), show: isAuth && !!node.accessRead && staff }
      ]
    },
    {
      title: 'People',
      items: [
        { ...sidebarEntry(SECTION.users), show: isAuth && !!user.accessRead },
        { ...sidebarEntry(SECTION.groups), show: isAuth && !!group.accessRead },
        { ...sidebarEntry(SECTION.selfRegister), show: isAuth && !!tradeshow.accessRead && tradeShow }
      ]
    },
    {
      title: 'Activity',
      items: [
        { ...sidebarEntry(SECTION.auditLog), show: isAuth && !!auditLog.accessRead },
        { ...sidebarEntry(SECTION.emailActivity), show: isAuth && !!user.accessUpdate },
        { ...sidebarEntry(SECTION.notificationActivity), show: isAuth && !!user.accessUpdate && notify }
      ]
    },
    {
      // The LMS connector (feat/lms-connector). While the staff-only switch is
      // closed (C1, view/lms-admin/lms-access.js) the whole entry is a system
      // administrator's or root's alone: absent for anyone else, not
      // disabled. It also needs the current organization to have the LMS
      // (lmsEnabled above). The LMS has no API keys entry of its own (Phase D
      // D7): its keys are core API keys, the organization's on API keys
      // above, a connection's in its drawer. The platform's LMS presets are
      // not here (owner 2026-10-20): they are an Internal tools entry in
      // Platform.
      title: 'Integrations',
      items: [
        // Whoever reads API keys, while the organization's API access is on
        // (apiKeysAllowed above).
        { ...sidebarEntry(SECTION.apiKeys), show: apiKeysAllowed },
        {
          title: SECTION.lms.text,
          Icon: SECTION.lms.Icon,
          show: isAuth && !!lms.accessRead && lmsAdminVisible(state) && lmsEnabled,
          items: [
            sidebarEntry(SECTION.lmsConnections),
            sidebarEntry(SECTION.lmsCourses),
            sidebarEntry(SECTION.lmsAccountLinks),
            sidebarEntry(SECTION.lmsSessions),
            sidebarEntry(SECTION.lmsActivity),
            sidebarEntry(SECTION.lmsUsage),
            sidebarEntry(SECTION.lmsSettings)
          ]
        }
      ]
    },
    {
      title: 'Settings',
      items: [
        { ...sidebarEntry(SECTION.organization), show: isAuth && !!organization.accessRead },
        { ...sidebarEntry(SECTION.authentication), show: isAuth && !!sso.accessRead },
        { title: 'Docs', Icon: IconDocs, onClick: handleDocs, show: isAuth && company && !!doc.accessRead }
      ]
    }
  ]

  const staffGroups = [
    {
      title: 'Organizations & people',
      items: [
        { ...sidebarEntry(SECTION.organizations), show: staff && !!sysOrganization.accessRead },
        { ...sidebarEntry(SECTION.logins), show: staff && !!sysLogin.accessRead },
        { ...sidebarEntry(SECTION.authSessions), show: staff && !!sysAuthStatus.accessRead }
      ]
    },
    {
      title: 'Sign-in & SSO',
      items: [
        { ...sidebarEntry(SECTION.ssoConnections), show: staff && !!sysProvider.accessRead },
        { ...sidebarEntry(SECTION.signInMethods), show: staff && !!sysProvider.accessRead },
        { ...sidebarEntry(SECTION.refusedSignIns), show: staff && !!sysAuthStatus.accessRead },
        { ...sidebarEntry(SECTION.refusedSignInLog), show: staff && !!sysAuthStatus.accessRead },
        // SSO organizations whose own SSO alert list is empty (owner
        // 2026-10-30: moved here from Alert routing's tabs).
        { ...sidebarEntry(SECTION.ssoAlertCoverage), show: staff && !!sysAlertRouting.accessRead }
      ]
    },
    {
      title: 'Monitoring',
      items: [
        { ...sidebarEntry(SECTION.errors), show: staff && !!sysErrors.accessRead },
        { ...sidebarEntry(SECTION.requestLog), show: staff && !!sysRequestLog.accessRead },
        { ...sidebarEntry(SECTION.staffAuditLog), show: staff && !!sysAuditLog.accessRead },
        { ...sidebarEntry(SECTION.auditRetention), show: staff && !!sysAuditRetention.accessRead }
      ]
    },
    {
      title: 'Alerts & email',
      items: [
        // Alert routing (owner 2026-10-30): a page each, like every other
        // area, never one page with tabs (alert-routing/paths.js).
        { ...sidebarEntry(SECTION.alertRoutes), show: staff && !!sysAlertRouting.accessRead },
        { ...sidebarEntry(SECTION.alertTargets), show: staff && !!sysAlertRouting.accessRead },
        { ...sidebarEntry(SECTION.alertConnections), show: staff && !!sysAlertRouting.accessRead },
        { ...sidebarEntry(SECTION.alertRuns), show: staff && !!sysAlertRouting.accessRead },
        { ...sidebarEntry(SECTION.staffEmailActivity), show: staff && !!sysSendgridEvent.accessRead },
        { ...sidebarEntry(SECTION.notifications), show: staff && !!sysNotify.accessRead && notify }
      ]
    },
    {
      // Apps (SYS_APP read) is not root-only, so it sits apart from the Root
      // group (owner 2026-10-07).
      title: 'Platform',
      items: [
        { ...sidebarEntry(SECTION.apps), show: staff && !!sysApp.accessRead },
        // Every organization's API keys and their usage (Phase D, Task 16).
        { ...sidebarEntry(SECTION.staffApiKeys), show: staff && !!sysApiKey.accessRead },
        // AI tools telemetry (spec 2026-10-20; contract §8, R32): sessions,
        // postmortems and findings with their processing marks, beside the
        // API keys whose telemetry key exports them.
        { ...sidebarEntry(SECTION.aiTools), show: staff && !!sysAiToolsTelemetry.accessRead },
        // Packages: EducationXR's Unity package registry (packages, versions,
        // publish keys). Hidden where the build has no packages service
        // (REACT_APP_API_PACKAGES unset: prod before its cutover).
        { ...sidebarEntry(SECTION.packages), show: staff && !!sysPackages.accessRead && packagesAvailable() },
        // Billing: the plan catalog and an organization's entitlements, read
        // only. Hidden where the build has no billing service
        // (REACT_APP_API_BILLING unset).
        { ...sidebarEntry(SECTION.billing), show: staff && !!sysBilling.accessRead && billingAvailable() },
        // The website's consent log: look up a consent ID, verify a signed
        // one, export a date range as CSV.
        { ...sidebarEntry(SECTION.consentLog), show: staff && !!sysConsentLog.accessRead },
        // The LMS connector's platform templates (owner 2026-10-20: they were
        // under Integrations > LMS). A system administrator holding the
        // SYS_LMS_PRESET scope (root reads every system scope), as the
        // server's preset routes need; platform-wide, so it never depends on
        // the current organization's LMS.
        { ...sidebarEntry(SECTION.lmsPresets), show: staff && !!sysLmsPreset.accessRead }
      ]
    },
    {
      // Root only: every entry needs root, so the tag is always true.
      title: 'Root',
      tag: 'ROOT',
      items: [
        { ...sidebarEntry(SECTION.systemRoles), show: root },
        { ...sidebarEntry(SECTION.systemRolePermissions), show: root },
        { ...sidebarEntry(SECTION.organizationRoles), show: root },
        { ...sidebarEntry(SECTION.organizationRolePermissions), show: root },
        { ...sidebarEntry(SECTION.systemAdmins), show: root },
        { ...sidebarEntry(SECTION.analyticsSetup), show: root }
      ]
    }
  ]

  const anyStaffEntry = staffGroups.some(g => g.items.some(item => item.show))

  // The entry lit for this address, among those shown (a collapse's pages
  // are shown with it).
  const shownLinks = [...(showStaff ? staffGroups : []), ...orgGroups]
    .flatMap(g => g.items.filter(item => item.show))
    .flatMap(item => (item.items ? item.items : [item]))
    .filter(item => item.to)
  const current = (selectedEntry(shownLinks, location) || {}).to

  return (
    <div className="edxr-sb d-flex flex-column bg-primary h-100">
      <div>
        <div className="border-bottom border-secondary" style={{ height: '58px' }}>
          <Brand />
        </div>
      </div>

      {(isAuth || staff) &&
        <div className="edxr-sb-top">
          {isAuth && <OrganizationCard name={state.organizationName || claims.organizationName} canSwitch={canSwitchOrganization(claims)} target={target} />}
          {staff && !impersonating && <StaffToolsButton on={staffTools.on} onToggle={toggleStaffTools} />}
        </div>}

      <nav className="edxr-sb-scroll flex-fill" aria-label="Admin menu" ref={navRef}>
        {showStaff && anyStaffEntry &&
          <section className="edxr-sb-staff" aria-labelledby={titleId}>
            <div className="edxr-sb-staff__head">
              <FontAwesomeIcon icon={faShieldRegular} className="edxr-sb-staff__shield" />
              <span className="edxr-sb-staff__text">
                <span className="edxr-sb-staff__title" id={titleId}>Internal tools</span>
              </span>
            </div>
            {staffGroups.map(g => <Group key={g.title} {...g} testId="sidebar-group-heading" target={target} current={current} />)}
          </section>}

        <div className="edxr-sb-org-menu">
          {orgGroups.map(g => <Group key={g.title} {...g} testId="sidebar-org-group-heading" target={target} current={current} />)}
        </div>
      </nav>

      {/* Web viewer's failure to open, in the shared launch-failure modal: the
          sidebar sits outside the page's NotificationProvider. */}
      {viewerLaunch.failure && <ViewerLaunchFailure failure={viewerLaunch.failure} onClose={viewerLaunch.dismiss} />}
    </div>
  )
}
